import type { Cart } from "./types";
import type { Product } from "~/product/types";

// Esquema versionado del carrito persistido. Si cambia la forma del carrito,
// subir la versión invalida los guardados viejos automáticamente.
const STORAGE_PREFIX = "pency.cart.v1.";
const CART_VERSION = 1;
// Descartamos carritos más viejos que 30 días para no revivir carritos ancestrales.
const CART_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const PRICE_EPSILON = 0.001;

interface PersistedCart {
  version: number;
  updatedAt: number;
  cart: Cart;
}

export interface ReconcileResult {
  cart: Cart;
  removed: { id: string; title: string }[];
  priceChanged: boolean;
}

// El carrito se namespacea por tenant: varias tiendas comparten el mismo origin
// (pency.app/{slug}), así que sin esto el carrito de una se filtraría a otra.
function storageKey(tenantId: string): string {
  return `${STORAGE_PREFIX}${tenantId}`;
}

export function readCart(tenantId: string): Cart | null {
  if (typeof window === "undefined" || !tenantId) return null;

  try {
    const raw = window.localStorage.getItem(storageKey(tenantId));
    if (!raw) return null;

    const parsed: PersistedCart = JSON.parse(raw);

    // Versión distinta o carrito vencido → descartar
    if (parsed.version !== CART_VERSION) return null;
    if (!parsed.updatedAt || Date.now() - parsed.updatedAt > CART_TTL_MS) {
      window.localStorage.removeItem(storageKey(tenantId));
      return null;
    }

    return parsed.cart ?? null;
  } catch {
    // JSON corrupto / localStorage inaccesible → degradar a carrito vacío
    return null;
  }
}

export function writeCart(tenantId: string, cart: Cart): void {
  if (typeof window === "undefined" || !tenantId) return;

  try {
    // Carrito vacío → borramos la key en vez de guardar {}
    if (!cart || Object.keys(cart).length === 0) {
      window.localStorage.removeItem(storageKey(tenantId));
      return;
    }

    const payload: PersistedCart = {
      version: CART_VERSION,
      updatedAt: Date.now(),
      cart,
    };

    window.localStorage.setItem(storageKey(tenantId), JSON.stringify(payload));
  } catch {
    // quota excedida / modo privado → ignorar
  }
}

export function clearCart(tenantId: string): void {
  if (typeof window === "undefined" || !tenantId) return;

  try {
    window.localStorage.removeItem(storageKey(tenantId));
  } catch {
    // ignorar
  }
}

const priceDiffers = (a: number, b: number): boolean =>
  Math.abs((a ?? 0) - (b ?? 0)) > PRICE_EPSILON;

// Reconcilia el carrito persistido contra los productos frescos de la página:
//  - refresca precio del producto y de las variantes seleccionadas,
//  - quita productos que quedaron no disponibles/ocultos,
//  - conserva productos que no vinieron en esta página (los valida el server).
export function reconcileCart(cart: Cart, products: Product[]): ReconcileResult {
  const reconciled: Cart = {};
  const removed: { id: string; title: string }[] = [];
  let priceChanged = false;

  Object.values(cart || {}).forEach((item) => {
    const fresh = products.find((product) => product.id === item.product.id);

    // No está en el catálogo cargado (p.ej. límite por tier): lo conservamos
    // tal cual; el pre-check / backstop del server es la autoridad final.
    if (!fresh) {
      reconciled[item.id] = item;
      return;
    }

    // Pasó a no disponible u oculto → lo quitamos y lo reportamos
    if (fresh.type === "unavailable" || fresh.type === "hidden") {
      removed.push({ id: fresh.id, title: fresh.title });
      return;
    }

    let itemChanged = priceDiffers(item.product.price, fresh.price);

    const nextItem = {
      ...item,
      // Producto fresco completo (precio, título, tipo, stock, imágenes)
      product: { ...fresh },
      // Variantes: mantenemos la selección del usuario, refrescando precios
      variants: item.variants?.map((cartVariant) => {
        const freshVariant = fresh.options?.find((v) => v.id === cartVariant.id);
        if (!freshVariant) return cartVariant;

        return {
          ...cartVariant,
          value: cartVariant.value?.map((selected) => {
            const freshOption = freshVariant.options.find(
              (option) => option.id === selected.id
            );
            if (!freshOption) return selected;
            if (priceDiffers(selected.price, freshOption.price)) {
              itemChanged = true;
            }
            return { ...selected, price: freshOption.price };
          }),
        };
      }),
    };

    if (itemChanged) priceChanged = true;

    reconciled[item.id] = nextItem;
  });

  return { cart: reconciled, removed, priceChanged };
}
